import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { Header } from '../components/layout/Header';
import { DESIGN_TOKENS } from '../design-system/tokens/colors';
import { apiClientBff } from '../services/apiBff';
import { API_ENDPOINTS } from '../config/api';

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!cooldown) return;
    const id = window.setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          window.clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  const validateEmail = () => {
    const value = email.trim();
    if (!value) {
      setEmailError(t('validation.required'));
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError(t('validation.invalid_email'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateEmail()) return;
    if (cooldown > 0 || isSubmitting) return;

    setIsSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await apiClientBff.authPost<{ message?: string }>(
        API_ENDPOINTS.AUTH.FORGOT_PASSWORD, 
        { email: email.trim().toLowerCase() }
      );

      let localizedMessage = t('forgot_password.email_sent_generic');

      if (response && response.message) {
        if (response.message.includes('If an account with that email exists')) {
          localizedMessage = t('forgot_password.email_sent_generic');
        } else {
          localizedMessage = response.message;
        }
      }

      setSuccessMessage(localizedMessage);
      setCooldown(30);
      setTimeout(() => {
        navigate('/auth/reset-password');
      }, 800);

    } catch (error: any) {
      // If error is 4xx (client error), we still show success message for security (blind forgot password)
      // unless it's a 500+ server error
      const status = error.status || 500;
      
      if (status >= 500) {
        setErrorMessage(t('forgot_password.error_generic'));
      } else {
        // Treat 4xx errors as "success" to avoid user enumeration
        setSuccessMessage(t('forgot_password.email_sent_generic'));
        setCooldown(30);
        setTimeout(() => {
          navigate('/auth/reset-password');
        }, 800);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header progress={0} />
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          pt: '88px',
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            p: { xs: 4, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#ffffff',
            minHeight: { xs: 'auto', md: '100vh' },
          }}
        >
          <Box
            sx={{
              maxWidth: '400px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ mb: 6 }}>
              <img
                src="/logo.svg"
                alt="MUU Logo"
                style={{ height: 48 }}
              />
            </Box>

            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              {t('forgot_password.title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, textAlign: 'center' }}
            >
              {t('forgot_password.subtitle')}
            </Typography>

            {successMessage && (
              <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
                {successMessage}
              </Alert>
            )}

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                {errorMessage}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              <TextField
                fullWidth
                label={t('login.email')}
                type="email"
                variant="outlined"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={validateEmail}
                error={!!emailError}
                helperText={emailError}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting || cooldown > 0}
                sx={{
                  py: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: '#0C5240',
                  boxShadow: '0 4px 12px rgba(12, 82, 64, 0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#064E3B',
                    boxShadow: '0 6px 20px rgba(12, 82, 64, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 8px rgba(12, 82, 64, 0.3)',
                  },
                  '&:disabled': {
                    backgroundColor: '#9ca3af',
                    boxShadow: 'none',
                    transform: 'none',
                  },
                }}
              >
                {cooldown > 0
                  ? t('forgot_password.submit_cooldown', { seconds: cooldown })
                  : t('forgot_password.submit')}
              </Button>

              <Button
                type="button"
                variant="text"
                fullWidth
                onClick={() => navigate('/auth')}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  color: DESIGN_TOKENS.colors.primary[700],
                }}
              >
                {t('forgot_password.back_to_login')}
              </Button>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[500]} 0%, ${DESIGN_TOKENS.colors.primary[600]} 50%, ${DESIGN_TOKENS.colors.primary[400]} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 6,
            minHeight: { xs: '60vh', md: '100vh' },
            borderBottomRightRadius: { xs: 0, md: '24px' },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: '24px',
              p: 6,
              maxWidth: '600px',
              width: '100%',
              boxShadow: '0 32px 64px rgba(0, 0, 0, 0.2)',
              position: 'relative',
            }}
            aria-hidden="true"
          >
            <Typography
              variant="body1"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                fontSize: '1.1rem',
                mb: 2,
                fontWeight: 500,
              }}
            >
              {t('forgot_password.helper_title')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                fontSize: '0.95rem',
              }}
            >
              {t('forgot_password.helper_text')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ForgotPasswordPage;
